import { Switch, Route, useLocation } from "wouter";//////
import posthog from "posthog-js";
import { isPostHogEnabled } from "./lib/posthog";
import { queryClient } from "./lib/queryClient";
import { kbPersistOptions } from "./lib/rqPersistOptions";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { ThemeProvider } from "@/components/ThemeProvider";
import { MastermindProvider, useMastermind } from "@/contexts/MastermindContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { GoogleConnectionProvider } from "@/contexts/GoogleConnectionContext";
import { WorkspaceProvider, useWorkspace } from "@/contexts/WorkspaceContext";
import { UserProfileProvider } from "@/contexts/UserProfileContext";
import { KnowledgeBaseProvider } from "@/contexts/KnowledgeBaseContext";
import { KnowledgeBaseLegendSync } from "@/components/KnowledgeBaseLegendSync";
import { ChatThreadsProvider } from "@/contexts/ChatThreadsContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { LibraryProvider } from "@/contexts/LibraryContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ImportProgressProvider } from "@/contexts/ImportProgressContext";
import { useCallback, useEffect, useState } from "react";
import { buildResetPasswordUrl, isPasswordRecoveryUrl } from "@/lib/authRecovery";
import { buildLoginUrlWithReturnTo, isInvitationSharePath, isPublicPath } from "@/lib/publicRoutes";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PanelRight } from "lucide-react";
import type { MastermindSpace } from "@shared/schema";
import { DocumentViewerProvider, useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { ChatWorkspace } from "@/pages/ChatWorkspace";
import WorkflowCanvasPage from "@/pages/WorkflowCanvasPage"; // Phase 4A (experimental)
import Dashboard from "@/pages/Dashboard";
import MastermindSpaces from "@/pages/MastermindSpaces";
import MastermindSpaceDetail from "@/pages/MastermindSpaceDetail";
import SpaceShareAcceptance from "@/pages/SpaceShareAcceptance";
import WorkspaceShareAcceptance from "@/pages/WorkspaceShareAcceptance";
import AgentShareAcceptance from "@/pages/AgentShareAcceptance";
import AgentFolderShareAcceptance from "@/pages/AgentFolderShareAcceptance";
import ThreadShareAcceptance from "@/pages/ThreadShareAcceptance";
import TopicShareAcceptance from "@/pages/TopicShareAcceptance";
import TrainsOfThought from "@/pages/TrainsOfThought";
import KnowledgeBaseDocuments from "@/pages/KnowledgeBaseDocuments";
import ReferenceDocuments from "@/pages/ReferenceDocuments";
import Library from "@/pages/Library";
import Home from "@/pages/Home";

import ModelSelector from "@/pages/ModelSelector";

import DailyDigest from "@/pages/DailyDigest";

import ExportImport from "@/pages/ExportImport";
import DeveloperApi from "@/pages/DeveloperApi";
import McpConnections from "@/pages/McpConnections";
import TokenSavings from "@/pages/TokenSavings";
import ShelfLibrary from "@/pages/ShelfLibrary";
import McpOAuthAuthorize from "@/pages/McpOAuthAuthorize";
import CodingStandards from "@/pages/CodingStandards";
import BrowserHistory from "@/pages/BrowserHistory";
import CommandCenter from "@/pages/CommandCenter";
import SmartTasks from "@/pages/SmartTasks";
import Reminders from "@/pages/Reminders";
import CreativeStudio from "@/pages/CreativeStudio";


import PlaybookBuilder from "@/pages/PlaybookBuilder";
import Automations from "@/pages/Automations";
import AgentMissions from "@/pages/AgentMissions";
import PerformanceInsights from "@/pages/PerformanceInsights";
import Settings from "@/pages/Settings";
import Stats from "@/pages/Stats";
import Pricing from "@/pages/Pricing";
import MultiModelChat from "@/pages/MultiModelChat";
import TagManagement from "@/pages/TagManagement";
import TagControlPanel from "@/pages/TagControlPanel";
import Workspaces from "@/pages/Workspaces";
import AiAgentsHub from "@/pages/AiAgentsHub";
import AgentSkillsPage from "@/pages/AgentSkillsPage";
import AgentControlPanel from "@/pages/AgentControlPanel";
import TopicsManagement from "@/pages/TopicsManagement";
import Threads from "@/pages/Threads";
import ResearchTemplates from "@/pages/ResearchTemplates";
import Templates from "@/pages/Templates";
import YouTubeAutomation from "@/pages/YouTubeAutomation";
import FirefliesMeetings from "@/pages/FirefliesMeetings";
import WorkflowRunsPage from "@/pages/WorkflowRunsPage";
import GoogleDriveKnowledge from "@/pages/GoogleDriveKnowledge";
import GoHighLevelHub from "@/pages/GoHighLevelHub";
import TelegramInbox from "@/pages/TelegramInbox";
import GoogleOAuthCallback from "@/pages/GoogleOAuthCallback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";




import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import LandingPage from "@/pages/LandingPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/not-found";
import { SourceViewerPage, DocumentViewerPage } from "@/pages/DocumentViewer.page";
import PublicDocumentSharePage from "@/pages/PublicDocumentSharePage";
import DocumentShareOpenPage from "@/pages/DocumentShareOpenPage";
import PluginStore from "@/pages/PluginStore";
import PluginDeveloperDocs from "@/pages/PluginDeveloperDocs";
import { PluginProvider, usePluginsOptional } from "@/plugins/PluginContext";
import { PluginPageRenderer } from "@/plugins/PluginPageRenderer";
import { AppSidebar } from "@/components/AppSidebar";
import { ChatEmbedView } from "@/components/ChatEmbedView";
import { LibrarySidebar } from "@/components/LibrarySidebar";
import { LibraryRouteDndWrapper } from "@/library/LibraryRouteDndWrapper";
import { RightSidebar } from "@/components/RightSidebar";
import { DocumentViewer } from "@/components/DocumentViewer";
import { ImportProgressIndicator } from "@/components/ImportProgressIndicator";
import { NotificationBell } from "@/components/NotificationBell";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { LowBalanceAlert } from "@/components/LowBalanceAlert";
import { MilestoneCelebration } from "@/components/MilestoneCelebration";
import { PwaUpdatePrompt } from "@/components/PwaUpdatePrompt";
import { SessionExpiredDialog } from "@/components/SessionExpiredDialog";
//redeployment of the 502 ring central inbox
import { useIsMobile } from "@/hooks/use-mobile";

const RIGHT_PANEL_MIN = 20;
const RIGHT_PANEL_MAX = 50;
const RIGHT_SIDEBAR_AUTOSAVE_ID = "app-right-sidebar";

function AuthRouteRedirect() {
  const [location, setLocation] = useLocation();
  useEffect(() => {
    // wouter's location is path-only; include the query string so deep links
    // like /chat?threadId=... survive the login round-trips
    setLocation(buildLoginUrlWithReturnTo(location, window.location.search));
  }, [location, setLocation]);
  return null;
}

/**
 * Built-in communication pages that moved into plugins. Each old URL
 * redirects to the plugin page, keeping the query string so OAuth landings
 * (?connected= / ?error=) and old links keep working. The old page components
 * stay in src/pages (unrouted) for now.
 */
function pluginRedirect(target: string) {
  return function PluginRedirect() {
    const [, setLocation] = useLocation();
    useEffect(() => {
      setLocation(`${target}${window.location.search}`, { replace: true });
    }, [setLocation]);
    return null;
  };
}
const RingCentralPluginRedirect = pluginRedirect("/plugins/ringcentral");
const GmailPluginRedirect = pluginRedirect("/plugins/gmail");
const ZoomPluginRedirect = pluginRedirect("/plugins/zoom");
const ClickUpPluginRedirect = pluginRedirect("/plugins/clickup");

function AuthRouter() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route component={AuthRouteRedirect} />
    </Switch>
  );
}

function MainRouter() {
  const plugins = usePluginsOptional();
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/home" component={Home} />
      <Route path="/chat" component={ChatWorkspace} />
      <Route path="/workflow-canvas/:id?" component={WorkflowCanvasPage} />{/* Phase 4A experimental */}
      <Route path="/your-chats" component={ChatEmbedView} />
      <Route path="/source/:id" component={SourceViewerPage} />
      <Route path="/document/:id" component={DocumentViewerPage} />
      <Route path="/documents/share/:token" component={DocumentShareOpenPage} />
      <Route path="/activity" component={Dashboard} />
      <Route path="/spaces/share/:token" component={SpaceShareAcceptance} />
      <Route path="/agents/share/:token" component={AgentShareAcceptance} />
      <Route path="/agents/folders/share/:token" component={AgentFolderShareAcceptance} />
      <Route path="/threads/share/:token" component={ThreadShareAcceptance} />
      <Route path="/topics/share/:token" component={TopicShareAcceptance} />
      <Route path="/workspaces/share/:token" component={WorkspaceShareAcceptance} />
      <Route path="/spaces/:id">
        {() => (
          <SectionErrorBoundary sectionName="Mastermind space detail">
            <MastermindSpaceDetail />
          </SectionErrorBoundary>
        )}
      </Route>
      <Route path="/spaces" component={MastermindSpaces} />
      <Route path="/trains" component={TrainsOfThought} />
      <Route path="/knowledge-base-documents" component={KnowledgeBaseDocuments} />
      <Route path="/library">
        {() => (
          <SectionErrorBoundary sectionName="Library">
            <Library />
          </SectionErrorBoundary>
        )}
      </Route>
      <Route path="/references" component={ReferenceDocuments} />
      <Route path="/stats" component={Stats} />
      <Route path="/model-selector" component={ModelSelector} />
      <Route path="/daily-digest" component={DailyDigest} />
      <Route path="/export-import" component={ExportImport} />
      <Route path="/developer-api" component={DeveloperApi} />
      <Route path="/mcp-oauth/authorize" component={McpOAuthAuthorize} />
      <Route path="/mcp-connections" component={McpConnections} />
      <Route path="/token-savings" component={TokenSavings} />
      <Route path="/shelf-library" component={ShelfLibrary} />
      <Route path="/coding-standards" component={CodingStandards} />
      <Route path="/browser-history" component={BrowserHistory} />
      <Route path="/command-center" component={CommandCenter} />
      <Route path="/tasks" component={SmartTasks} />
      <Route path="/reminders" component={Reminders} />
      <Route path="/creative-studio" component={CreativeStudio} />
      <Route path="/workspaces" component={Workspaces} />
      <Route path="/tags" component={TagManagement} />
      <Route path="/tags/:id/edit" component={TagControlPanel} />
      <Route path="/topics" component={TopicsManagement} />
      <Route path="/threads" component={Threads} />
      <Route path="/ai-agents/skills" component={AgentSkillsPage} />
      <Route path="/ai-agents" component={AiAgentsHub} />
      <Route path="/ai-agents/:id/edit" component={AgentControlPanel} />
      <Route path="/research-templates" component={ResearchTemplates} />
      <Route path="/templates" component={Templates} />
      <Route path="/youtube" component={YouTubeAutomation} />
      <Route path="/fireflies" component={FirefliesMeetings} />
      <Route path="/workflows/runs/:runId" component={WorkflowRunsPage} />
      <Route path="/communication/ringcentral" component={RingCentralPluginRedirect} />
      <Route path="/communication/gohighlevel" component={GoHighLevelHub} />
      <Route path="/communication/telegram" component={TelegramInbox} />
      <Route path="/communication/gmail" component={GmailPluginRedirect} />
      <Route path="/google-oauth/callback" component={GoogleOAuthCallback} />
      <Route path="/communication/zoom" component={ZoomPluginRedirect} />
      <Route path="/communication/clickup" component={ClickUpPluginRedirect} />
      <Route path="/communication/google-drive" component={GoogleDriveKnowledge} />
      <Route path="/playbooks" component={PlaybookBuilder} />
      <Route path="/automations" component={Automations} />
      <Route path="/missions" component={AgentMissions} />
      <Route path="/performance" component={PerformanceInsights} />
      <Route path="/credits">
        {() => {
          const [, setLocation] = useLocation();
          useEffect(() => { setLocation("/settings?tab=subscription"); }, []);
          return null;
        }}
      </Route>
      <Route path="/pricing" component={Pricing} />
      <Route path="/compare-models" component={MultiModelChat} />
      <Route path="/settings">
        {() => (
          <SectionErrorBoundary sectionName="Settings">
            <Settings />
          </SectionErrorBoundary>
        )}
      </Route>
      <Route path="/plugins/docs" component={PluginDeveloperDocs} />
      <Route path="/plugins" component={PluginStore} />
      {(plugins?.routes ?? []).map((pluginRoute) => (
        <Route key={`${pluginRoute.pluginId}:${pluginRoute.path}`} path={pluginRoute.path}>
          {() => (
            <PluginPageRenderer pluginId={pluginRoute.pluginId} path={pluginRoute.path} />
          )}
        </Route>
      ))}
      <Route component={NotFound} />
    </Switch>
  );
}

function AppContent() {
  const isMobile = useIsMobile();
  const { user, isLoading } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { selectedItem, isExpanded, openItem, closeItem, toggleExpand } = useDocumentViewer();
  const { isCollapsed, setIsCollapsed, currentSpaceId, currentTopicId } = useMastermind();
  const [location] = useLocation();
  const [isRecoveryRedirecting, setIsRecoveryRedirecting] = useState(
    () => isPasswordRecoveryUrl() && !window.location.pathname.startsWith("/reset-password")
  );

  useEffect(() => {
    if (isPasswordRecoveryUrl() && !location.startsWith("/reset-password")) {
      setIsRecoveryRedirecting(true);
      window.location.replace(buildResetPasswordUrl());
    }
  }, [location]);

  // When opening a document from the right panel while on a space (or topic), auto-link it to that space/topic
  const handleOpenItem = useCallback(
    (id: string, type: "document" | "source") => {
      if (type === "document" && currentSpaceId) {
        knowledgeBaseService
          .linkDocumentToSpaceTopic(id, currentSpaceId, currentTopicId ?? undefined)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
          })
          .catch(() => {});
      }
      openItem(id, type);
    },
    [currentSpaceId, currentTopicId, openItem]
  );
  const isLibraryRoute = location.startsWith("/library");
  const isFullHeightChatRoute =
    location === "/" || location === "/home" || location === "/chat";
  const isFullHeightPluginRoute = location.startsWith("/plugins/standards-canon");
  const isFullHeightMainRoute = isFullHeightChatRoute || isFullHeightPluginRoute;

  // PostHog: capture page view on every route change
  useEffect(() => {
    if (isPostHogEnabled() && typeof posthog?.capture === "function") {
      const url = window.location.origin + location;
      posthog.capture("$pageview", { $current_url: url });
    }
  }, [location]);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("onboarding_dismissed") === "true";
    }
    return false;
  });

  // Check if user has any personal spaces to determine if they're new (onboarding)
  const { data: spaces } = useQuery<MastermindSpace[]>({
    queryKey: ["/api/mastermind-spaces-personal"],
    queryFn: async () => {
      const { mastermindSpacesService } = await import("@/services/mastermindSpacesService");
      return mastermindSpacesService.getMastermindSpaces(null);
    },
    enabled: !!user && !onboardingDismissed,
  });

  // Show onboarding for new users (no spaces yet)
  useEffect(() => {
    if (user && !onboardingDismissed && spaces !== undefined && spaces.length === 0) {
      setShowOnboarding(true);
    }
  }, [user, spaces, onboardingDismissed]);

  const handleOnboardingComplete = () => {
    localStorage.setItem("onboarding_dismissed", "true");
    setOnboardingDismissed(true);
    setShowOnboarding(false);
  };

  const handleOnboardingClose = (open: boolean) => {
    if (!open) {
      localStorage.setItem("onboarding_dismissed", "true");
      setOnboardingDismissed(true);
    }
    setShowOnboarding(open);
  };

  if (isLoading || isRecoveryRedirecting) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">{isRecoveryRedirecting ? "Redirecting to password reset..." : "Loading..."}</div>
      </div>
    );
  }

  const isPrivacyRoute = location.startsWith("/privacy");
  const isDocumentPublicShareRoute = location.startsWith("/documents/share/");
  const isInvitationShareRoute = isInvitationSharePath(location);
  const isAuthRoute =
    isPublicPath(location) &&
    location !== "/" &&
    !isPrivacyRoute &&
    !isDocumentPublicShareRoute;

  if (!user && location === "/") {
    return <LandingPage />;
  }

  if (!user && isDocumentPublicShareRoute) {
    return <PublicDocumentSharePage />;
  }

  // Invitation pages show their own "Sign in required" UI; do not redirect via AuthRouter.
  if (!user && isInvitationShareRoute) {
    return <MainRouter />;
  }

  if (isPrivacyRoute) {
    return <PrivacyPolicy />;
  }

  if (!user || isAuthRoute) {
    return <AuthRouter />;
  }

  return (
    <SidebarProvider defaultOpen={false}>
      <LowBalanceAlert />
      <div className="flex h-screen w-full">
        {/* Non-library routes use regular sidebar */}
        {!isLibraryRoute && <AppSidebar />}
        <OnboardingWizard
          open={showOnboarding}
          onOpenChange={handleOnboardingClose}
          onComplete={handleOnboardingComplete}
        />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile header with sidebar trigger */}
          <header className="md:hidden flex items-center justify-between gap-2 p-2 border-b bg-background sticky top-0 z-40">
            <SidebarTrigger data-testid="button-mobile-sidebar" />
            <div className="flex items-center gap-1">
              <ImportProgressIndicator />
              <NotificationBell />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsCollapsed(!isCollapsed)}
                data-testid="button-mobile-toggle-right-panel"
                title={isCollapsed ? "Show right panel" : "Hide right panel"}
              >
                <PanelRight className="h-4 w-4"/>
              </Button>
            </div>
          </header>
          
          {/* Main content area */}
          <div className="flex-1 overflow-hidden">
            {/* Desktop: Resizable panels, Mobile: Full width main content only */}
            <div className="hidden md:block h-full">
              <LibraryRouteDndWrapper when={isLibraryRoute}>
              <ResizablePanelGroup
                id="app-main-panels"
                direction="horizontal"
                className="h-full"
                autoSaveId={!isCollapsed ? RIGHT_SIDEBAR_AUTOSAVE_ID : undefined}
              >
                {/* Library sidebar as resizable panel, THis is an update to run deployment to vercel from github */}
                {isLibraryRoute && (
                  <>
                    <ResizablePanel id="library-sidebar" order={0} defaultSize={20} minSize={12} maxSize={35}>
                      <SectionErrorBoundary sectionName="Library sidebar">
                        <LibrarySidebar />
                      </SectionErrorBoundary>
                    </ResizablePanel>
                    <ResizableHandle withHandle className="w-1 hover:bg-primary/20 transition-colors" />
                  </>
                )}
                <ResizablePanel id="main-content" order={1} defaultSize={isLibraryRoute ? (isCollapsed ? 80 : (selectedItem ? 40 : 50)) : (isCollapsed ? 100 : (selectedItem ? 50 : 70))} minSize={30} style={{ minWidth: 0 }}>
                  <div className="flex flex-col h-full relative" style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                    {location !== "/chat" && (
                      <div className="absolute top-2 right-3 z-50 flex items-center gap-1.5 bg-background/80 backdrop-blur-sm rounded-lg px-1 py-0.5">
                        <ImportProgressIndicator />
                        <NotificationBell />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setIsCollapsed(!isCollapsed)}
                          data-testid="button-toggle-right-panel"
                          title={isCollapsed ? "Show right panel" : "Hide right panel"}
                        >
                          <PanelRight className="h-4 w-4"/>
                        </Button>
                      </div>
                    )}
                    <main
                      className={`flex-1 min-w-0 ${isFullHeightMainRoute ? "overflow-hidden" : "overflow-auto"}`}
                      style={{ width: "100%" }}
                    >
                      <div className="h-full min-h-0 min-w-0" style={{ width: "100%", maxWidth: "100%" }}>
                        <MainRouter />
                      </div>
                    </main>
                  </div>
                </ResizablePanel>
                {!isCollapsed && (
                  <>
                    <ResizableHandle withHandle className="w-1 hover:bg-primary/20 transition-colors" />
                    <ResizablePanel
                      id="right-panel"
                      order={2}
                      defaultSize={selectedItem ? 50 : 30}
                      minSize={RIGHT_PANEL_MIN}
                      maxSize={RIGHT_PANEL_MAX}
                      className="min-h-0 min-w-0"
                    >
                      {selectedItem ? (
                        <DocumentViewer
                          key={`${selectedItem.type}-${selectedItem.id}`}
                          itemId={selectedItem.id}
                          itemType={selectedItem.type}
                          onClose={closeItem}
                          isExpanded={isExpanded}
                          onToggleExpand={toggleExpand}
                        />
                      ) : (
                        <RightSidebar onItemClick={openItem} />
                      )}
                    </ResizablePanel>
                  </>
                )}
              </ResizablePanelGroup>
              </LibraryRouteDndWrapper>
            </div>
            
            {/* Mobile: Simple full-width layout */}
            <div
              className={`md:hidden h-full ${isFullHeightMainRoute ? "overflow-hidden" : "overflow-auto"}`}
            >
              <div className="h-full w-full">
                <MainRouter />
              </div>

              {/* Mobile Right Sidebar Sheet */}
              <Sheet open={isMobile && !isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)} modal={false}>
                <SheetContent side="right" hideOverlay className="p-0 w-[85vw] sm:w-[400px] border-l shadow-2xl">
                  <div className="h-full">
                    {selectedItem ? (
                      <DocumentViewer
                        key={`mobile-${selectedItem.type}-${selectedItem.id}`}
                        itemId={selectedItem.id}
                        itemType={selectedItem.type}
                        onClose={() => {
                          closeItem();
                          // Keep the sidebar open even if document is closed on mobile
                        }}
                        isExpanded={false}
                        onToggleExpand={() => {}}
                      />
                    ) : (
                      <RightSidebar onItemClick={handleOpenItem} />
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function App() {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={kbPersistOptions}>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <AuthProvider>
            <GoogleConnectionProvider>
            <WorkspaceProvider>
              <UserProfileProvider>
                <ChatThreadsProvider>
                  <KnowledgeBaseProvider>
                    <KnowledgeBaseLegendSync />
                    <OnboardingProvider>
                      <MastermindProvider>
                      <DocumentViewerProvider>
                        <ImportProgressProvider>
                          <LibraryProvider>
                            <PluginProvider>
                              <AppContent />
                            </PluginProvider>
                            <MilestoneCelebration />
                            <PwaUpdatePrompt />
                            <SessionExpiredDialog />
                            <Toaster />
                          </LibraryProvider>
                        </ImportProgressProvider>
                      </DocumentViewerProvider>
                      </MastermindProvider>
                    </OnboardingProvider>
                  </KnowledgeBaseProvider>
                </ChatThreadsProvider>
              </UserProfileProvider>
            </WorkspaceProvider>
            </GoogleConnectionProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
